// The "Branch Validation Inbox" tab. It is NOT written into any of the lists
// below — it is ADDED ON TOP of whichever list the user's role already gave
// them, so they keep Summary / Sanctioned Loans / MIS / etc. and simply gain
// this one extra tab.
//
// It used to live inside NAVIGATIONS_UBI_FRESH_MAKER_CHECKER, which tied it to
// roleId 11 (Maker) and 12 (Checker). It is now driven by the `approvalStage`
// field on the /login response instead, for EVERY roleId — so a Branch Admin
// whose approvalStage is 'checker' gets the tab, and a roleId 11 whose
// approvalStage is 'unknown' does not.
const BRANCH_VALIDATION_INBOX = {
  name: "Branch Validation Inbox",
  icon: "../../assets/svg/white/baseline_journey_view_white_24dp.png",
  link: "fresh-app/view-review-journey",
  isSelected: false,
};

/**
 * Takes the menu the role selected and returns it with Branch Validation Inbox
 * appended, if approvalStage says this user is a maker or a checker. Any other
 * value — 'unknown', missing, or an older backend that doesn't send the field —
 * gives back the role's menu untouched.
 *
 * This is a FUNCTION rather than a condition written into the arrays below,
 * because the arrays are `const`s: JavaScript evaluates them once, the moment
 * this file is first imported. FreshAppModule is imported statically by
 * app-routing.module.ts (`loadChildren: () => FreshAppModule` points at an
 * already-imported class, it is not a real lazy `import()`), so that moment is
 * app BOOT — before anyone has logged in and before approvalStage exists in
 * sessionStorage. A condition baked into the arrays would read an empty value
 * once and the tab would never appear for anybody. Reading it inside a function
 * defers the check to when the menu is actually built, after login.
 *
 * Returns a NEW array instead of pushing into the one passed in: these lists are
 * shared singletons, so a push would stick and the tab would appear twice on the
 * next navigation into the shell — and would leak to the next user who logs in
 * without a full page reload.
 */
export function withValidationInbox(navList: any[]) {
  if (!navList) { return navList; }

  const stage = (sessionStorage.getItem('approvalStage') || '').trim().toLowerCase();
  if (stage !== 'maker' && stage !== 'checker') { return navList; }

  // Guard so it can't double up if a list ever gains this link again.
  if (navList.some(tab => tab && tab.link === BRANCH_VALIDATION_INBOX.link)) {
    return navList;
  }

  return [...navList, BRANCH_VALIDATION_INBOX];
}

export const NAVIGATIONS_UBI_FRESH = [
  {
    name: "Summary",
    icon: '../../assets/svg/demography_FILL1_wght600_GRAD0_opsz24.svg',
    link: "fresh-app/view-summary",
    isSelected: false,
  },
  {
    name: "Sanctioned Loans",
    icon: "../../assets/svg/white/baseline_credit_score_white_24dp.png",
    submenu: [
      {name:"ETB", link:'view-etb',isSubMenuSelected:false},
      {name:"NTB", link: "view-ntb",isSubMenuSelected:false},
    ],
    isSubmenuVisible: false,
    isSelected: false,
  },

  {
    name: "Disbursed Loans",
    icon: "../../assets/svg/white/baseline_check_circle_white_24dp.png",
    link: "fresh-app/view-disbursed",
    isSelected: false,
  },
  {
    name: "Incomplete Journey",
    icon: "../../assets/svg/white/baseline_pending_actions_white_24dp.png",
    link: "fresh-app/view-incomplete-journey",
    isSelected: false,
  },
  {
    name: "Failure Cases",
    icon: "../../assets/svg/white/baseline_feedback_white_24dp.png",
    link: "fresh-app/view-failure-page",
    isSelected: false,
  },
  {
    name: "MIS",
    icon: "../../assets/svg/white/baseline_leaderboard_white_24dp.png",
    link: "fresh-app/view-report-page",
    isSelected: false,
  },
];

export const NAVIGATIONS_UBI_FRESH_ADMIN = [
  {
    name: "Summary",
    icon: '../../assets/svg/demography_FILL1_wght600_GRAD0_opsz24.svg',
    link: "fresh-app/view-summary",
    isSelected: false,
  },
  {
    name: "Sanctioned Loans",
    icon: "../../assets/svg/white/baseline_credit_score_white_24dp.png",
    submenu: [
      {name:"ETB", link:'view-etb',isSubMenuSelected:false},
      {name:"NTB", link: "view-ntb",isSubMenuSelected:false},
    ],
    isSubmenuVisible: false,
    isSelected: false,
  },

  {
    name: "Disbursed Loans",
    icon: "../../assets/svg/white/baseline_check_circle_white_24dp.png",
    link: "fresh-app/view-disbursed",
    isSelected: false,
  },
  {
    name: "Incomplete Journey",
    icon: "../../assets/svg/white/baseline_pending_actions_white_24dp.png",
    link: "fresh-app/view-incomplete-journey",
    isSelected: false,
  },
  {
    name: "Failure Cases",
    icon: "../../assets/svg/white/baseline_feedback_white_24dp.png",
    link: "fresh-app/view-failure-page",
    isSelected: false,
  },
  {
    name: "MIS",
    icon: "../../assets/svg/white/baseline_leaderboard_white_24dp.png",
    link: "fresh-app/view-report-page",
    isSelected: false,
  },
  // {
  //   name: 'Reconciliation Reports',
  //   icon: '../../assets/svg/lab_profile_FILL1_wght600_GRAD0_opsz24.svg',
  //   link: 'fresh-app/view-recon',
  //   isSelected: false
  // },
  //  {
  //   name:"Review Inbox",
  //   icon: "../../assets/svg/white/baseline_leaderboard_white_24dp.png",
  //   link: "fresh-app/view-review-journey",
  //   isSelected: false,
  // },
  {
    name: 'Stale Users Report',
    icon: '../../assets/svg/white/baseline_pending_actions_white_24dp.png',
    link: 'fresh-app/stale-users-report',
    isSelected: false
  },

];

export const NAVIGATIONS_UBI_SUPER_ADMIN = [
  {
    name: "Summary",
    icon: '../../assets/svg/demography_FILL1_wght600_GRAD0_opsz24.svg',
    link: "fresh-app/view-summary",
    isSelected: false,
  },
  {
    name: "Sanctioned Loans",
    icon: "../../assets/svg/white/baseline_credit_score_white_24dp.png",
    submenu: [
      {name:"ETB", link:'view-etb',isSubMenuSelected:false},
      {name:"NTB", link: "view-ntb",isSubMenuSelected:false},
    ],
    isSubmenuVisible: false,
    isSelected: false,
  },

  {
    name: "Disbursed Loans",
    icon: "../../assets/svg/white/baseline_check_circle_white_24dp.png",
    link: "fresh-app/view-disbursed",
    isSelected: false,
  },
  {
    name: "Incomplete Journey",
    icon: "../../assets/svg/white/baseline_pending_actions_white_24dp.png",
    link: "fresh-app/view-incomplete-journey",
    isSelected: false,
  },
  {
    name: "Failure Cases",
    icon: "../../assets/svg/white/baseline_feedback_white_24dp.png",
    link: "fresh-app/view-failure-page",
    isSelected: false,
  },
  {
    name: "MIS",
    icon: "../../assets/svg/white/baseline_leaderboard_white_24dp.png",
    link: "fresh-app/view-report-page",
    isSelected: false,
  },
  // {
  //   name: 'Reconciliation Reports',
  //   icon: '../../assets/svg/lab_profile_FILL1_wght600_GRAD0_opsz24.svg',
  //   link: 'fresh-app/view-recon',
  //   isSelected: false
  // },
  {
    name: 'Error log',
    icon: '../../assets/svg/lab_profile_FILL1_wght600_GRAD0_opsz24.svg',
    link: 'fresh-app/error-log',
    isSelected: false
  },
];

export const NAVIGATIONS_UBI_FRESH_MAKER_CHECKER= [
  {
    name: "Summary",
    icon: '../../assets/svg/demography_FILL1_wght600_GRAD0_opsz24.svg',
    link: "fresh-app/view-summary",
    isSelected: false,
  },
  {
    name: "Sanctioned Loans",
    icon: "../../assets/svg/white/baseline_credit_score_white_24dp.png",
    submenu: [
      {name:"ETB", link:'view-etb',isSubMenuSelected:false},
      {name:"NTB", link: "view-ntb",isSubMenuSelected:false},
    ],
    isSubmenuVisible: false,
    isSelected: false,
  },

  {
    name: "Disbursed Loans",
    icon: "../../assets/svg/white/baseline_check_circle_white_24dp.png",
    link: "fresh-app/view-disbursed",
    isSelected: false,
  },
  {
    name: "Incomplete Journey",
    icon: "../../assets/svg/white/baseline_pending_actions_white_24dp.png",
    link: "fresh-app/view-incomplete-journey",
    isSelected: false,
  },
  {
    name: "Failure Cases",
    icon: "../../assets/svg/white/baseline_feedback_white_24dp.png",
    link: "fresh-app/view-failure-page",
    isSelected: false,
  },
  {
    name: "MIS",
    icon: "../../assets/svg/white/baseline_leaderboard_white_24dp.png",
    link: "fresh-app/view-report-page",
    isSelected: false,
  },
  // "Branch Validation Inbox" used to be listed here, which is what limited it
  // to Maker/Checker. It is now appended by withValidationInbox() at the top of
  // this file, based on approvalStage, for every role.
  // {
  //   name: 'Stale Users Report',
  //   icon: '../../assets/svg/white/baseline_pending_actions_white_24dp.png',
  //   link: 'admin/stale-users-report',
  //   isSelected: false
  // },
];